import React, { useEffect, useState, useContext, useRef } from 'react';
import { UserDetails, getDecryptedValue, apiResponseSafeCheck, unAuthorizedErrorHandler } from 'common';
import { GlobalContext } from 'context';
import {
  App,
  ChatbotCXOneConfig,
  SessionContext,
  eventOn,
  eventOff,
  USER_QUERY_SENT_EVENT,
  UNAUTHORIZED_CHATBOT_EVENT,
  STORED_TRANSCRIPT_EVENT,
  CLEAR_TRANSCRIPT_EVENT,
  StoredTranscriptMessage,
} from '@sentara-health/ui-ai-chatbot-web';
import type {
  AppAgentConfig,
  ChatbotFeatureFlags,
} from '@sentara-health/ui-ai-chatbot-web';
import type { SatmetrixConfig } from '@sentara-health/platform-chatbot-survey';
import { useTranslation } from 'react-i18next';

const normalizeDecryptedValue = (value: string | null): string => {
  if (!value) return '';

  try {
    const parsed = JSON.parse(value);
    if (typeof parsed === 'string' || typeof parsed === 'number') {
      return String(parsed);
    }
  } catch {}

  return value;
};

const getGroupLob = (encodedData: unknown): string => {
  if (typeof encodedData !== 'string' || !encodedData) return '';

  try {
    return JSON.parse(encodedData)?.custom?.groupLob ?? '';
  } catch {
    return '';
  }
};

const AiChatbot = ({ onMinimize, minimizeChat }: any) => {
  const { t } = useTranslation();
  const [chatBotAiConfig, setChatBotAiConfig] = useState<AppAgentConfig>(
    {} as AppAgentConfig
  );
  const [surveyConfig, setSurveyConfig] = useState<SatmetrixConfig>(
    {} as SatmetrixConfig
  );
  const {
    loginMemberId,
    getAuthToken,
    getFeature,
    serviceNumber,
    logger,
    monitoring,
    xauthToken,
    xsentaraTokenID,
    userEncodedData,
  } = useContext(GlobalContext);

  const [agentPhone, setAgentPhone] = useState<any>({});
  const [threadId] = useState<string>(`session_${Date.now()}`);
  let planType = '';
  if (getFeature?.HomeAddressMedicaid === true) {
    planType = 'MEDICAID';
  } else if (getFeature?.HomeAddressDSNP === true) {
    planType = 'DSNP';
  } else {
    planType = 'COMMERCIAL';
  }

  const [cxoneConfig, setCxoneConfig] = useState<ChatbotCXOneConfig | null>(
    null
  );
  const [sessionContext, setSessionContext] = useState<SessionContext | null>(
    null
  );
  const [storedTranscripts, setStoredTranscripts] = useState<
    StoredTranscriptMessage[] | null
  >(null);
  const [storedSessionId, setStoredSessionId] = useState<string | null>(null);
  const groupLob = getGroupLob(userEncodedData);

  useEffect(() => {
    if (apiResponseSafeCheck(serviceNumber)) {
      const getPhone = JSON.parse(serviceNumber ?? '{}');
      setAgentPhone(getPhone?.data);
    }
  }, [serviceNumber]);

  const sessionContextRef = useRef<SessionContext | null>(null);
  useEffect(() => {
    sessionContextRef.current = sessionContext;
  }, [sessionContext]);

  // Subscribe to REFRESH_TOKEN_CALLBACK event
  useEffect(() => {
    const handleRefreshTokenCallback = () => {
      console.log('USER_QUERY_SENT_EVENT received from AI chatbot lib:');
    };

    const handleUnAuthorizedCallback = () => {
      unAuthorizedErrorHandler();
    };

    const handleStoredTranscript = (data: {
      messages: readonly unknown[];
      sessionId: string;
    }) => {
      if (data?.messages?.length > 0 && data?.sessionId) {
        localStorage.setItem('storedTranscript', JSON.stringify(data));
        localStorage.setItem(
          'storedUserId',
          sessionContextRef.current?.userId || ''
        );
      }
    };
    const handleClearTranscript = () => {
      console.log(
        'CLEAR_TRANSCRIPT_EVENT event received from ai chatbot library'
      );
      localStorage.removeItem('storedTranscript');
      localStorage.removeItem('storedUserId');
    };

    eventOn(USER_QUERY_SENT_EVENT, handleRefreshTokenCallback);
    eventOn(UNAUTHORIZED_CHATBOT_EVENT, handleUnAuthorizedCallback);
    eventOn(STORED_TRANSCRIPT_EVENT, handleStoredTranscript);
    eventOn(CLEAR_TRANSCRIPT_EVENT, handleClearTranscript);
    return () => {
      eventOff(USER_QUERY_SENT_EVENT, handleRefreshTokenCallback);
      eventOff(UNAUTHORIZED_CHATBOT_EVENT, handleUnAuthorizedCallback);
      eventOff(STORED_TRANSCRIPT_EVENT, handleStoredTranscript);
      eventOff(CLEAR_TRANSCRIPT_EVENT, handleClearTranscript);
    };
  }, []);
const getStoredTranscript = () => {
    const storedTranscript = localStorage.getItem('storedTranscript');
    const parsedStoredTranscript = storedTranscript
      ? JSON.parse(storedTranscript)
      : null;
    const parsedStoredSessionId = parsedStoredTranscript?.sessionId || '';
    const parsedStoredMessages = (parsedStoredTranscript?.messages || []).slice(
      4
    );
    return {
      transcriptContent: parsedStoredMessages,
      sessionId: parsedStoredSessionId,
    };
  };

    useEffect(() => {
    if (!sessionContextRef.current) {
      return;
    }

    try {
      const storedUserId = localStorage.getItem('storedUserId');
      const { transcriptContent, sessionId } = getStoredTranscript();

      if (storedUserId === sessionContext?.userId) {
        setStoredTranscripts(transcriptContent);
        setStoredSessionId(sessionId);
      } else {
        setStoredTranscripts([]);
        setStoredSessionId('');
      }
    } catch {
      setStoredTranscripts([]);
      setStoredSessionId('');
    }
  }, [sessionContext?.userId]);

  useEffect(() => {
    if (!loginMemberId) return;
    (async () => {
      const authToken = getAuthToken;
      const PersonalInfo = await getDecryptedValue(UserDetails.guest);
      const userData = JSON.parse(PersonalInfo ?? '');
      const xAuthTokenNew = userData?.xAuthToken ?? '';
      const xSentaraTokenIDNew = userData?.xSentaraTokenID ?? '';

      setChatBotAiConfig({
        agentEndpoint:
          process.env.REACT_APP_CHATBOT_ORCHESTRATOR_END_POINT ?? '',
        orchestratorEndpoint:
          process.env.REACT_APP_CHATBOT_ORCHESTRATOR_END_POINT ?? '',
        version: process.env.REACT_APP_AGENT_VERSION ?? '',
        agentId: process.env.REACT_APP_AGENT_ID ?? '',
        threadId: threadId,
        clientId: process.env.REACT_APP_CLIENT_ID ?? '',
        tenantId: process.env.REACT_APP_TENANT_ID ?? '',
        memberId: loginMemberId,
        planType: planType,
        firstName: userData?.user?.firstName,
        salesforceMemberId: loginMemberId,
        headers: {
          'Content-Type': 'application/json',
          'Access-Control-Allow-Origin': '*',
          Authorization: `Bearer ${authToken}`,
          'x-sentara-auth-idtoken': xAuthTokenNew,
          'x-sentara-tokenid': xSentaraTokenIDNew,
          'Ocp-Apim-Subscription-Key':
            process.env.REACT_APP_CHATBOT_ORCHESTRATOR_SUBSCRIPTION_KEY ?? '',
          'Ocp-Apim-Subscription-Key-live-agent':
            process.env.REACT_APP_CHATBOT_SURVEY_API_KEY ||
            process.env.REACT_APP_CHATBOT_ORCHESTRATOR_SUBSCRIPTION_KEY ||
            '',
        },
        feedbackApiUrl: process.env.REACT_APP_CHATBOT_FEEDBACK_API_URL || '',
        feedbackSubscriptionKey:
          process.env.REACT_APP_CHATBOT_FEEDBACK_SUBSCRIPTION_KEY || '',
        lob: groupLob,
      });
      setSurveyConfig({
        apiUrl: process.env.REACT_APP_CHATBOT_SURVEY_URL ?? '',
        apiKey: process.env.REACT_APP_CHATBOT_SURVEY_API_KEY ?? '',
        satmetrixKey: process.env.REACT_APP_CHATBOT_SATMETRIX_KEY ?? '',
        satmetrixSecret: process.env.REACT_APP_CHATBOT_SATMETRIX_SECRET ?? '',
        userSessionId: loginMemberId,
        authToken: xauthToken,
        authIdToken: xAuthTokenNew,
        tokenId: xSentaraTokenIDNew,
        firstName: userData?.user?.firstName ?? 'User',
        lastName: userData?.user?.lastName ?? '',
        email: userData?.user?.emailId ?? '',
        forceAgentMode:
          process.env.REACT_APP_SURVEY_FORCE_AGENT_MODE === 'true',
        surveyId: process.env.REACT_APP_CHATBOT_SURVEY_ID || '',
        agentSurveyId: process.env.REACT_APP_LIVE_AGENT_ID || '',
      });

      setCxoneConfig({
        baseUrl: process.env.REACT_APP_AZURE_FUNCTIONS_ENDPOINT || '',
        wsBaseUrl: process.env.REACT_APP_AZURE_FUNCTIONS_ENDPOINT || '',
        // authToken: `Bearer ${authToken}`,
        headers: {
          'Content-Type': 'application/json',
          'Access-Control-Allow-Origin': '*',
          'x-sentara-auth-idtoken': xAuthTokenNew,
          'x-sentara-tokenid': xSentaraTokenIDNew,
          'Ocp-Apim-Subscription-Key':
            process.env.REACT_APP_AZURE_FUNCTIONS_SUBSCRIPTION_KEY ||
            process.env.REACT_APP_APIM_SUBS_KEY ||
            '',
        },
        endSessionPath: '/httpOnEndSessionV2',
        pointOfContact: process.env.REACT_APP_CXONE_POC,
        businessUnit: process.env.REACT_APP_CXONE_BUSINESS_UNIT,
      });

      setSessionContext({
        memberId: loginMemberId,
        normalUserId: normalizeDecryptedValue(
          await getDecryptedValue(UserDetails.id)
        ),
        userId: normalizeDecryptedValue(
          await getDecryptedValue(UserDetails.usedId)
        ),
        localPhone:
          agentPhone?.benefitPhoneLocal ??
          localStorage.getItem('global_phone_1800'),
        tollFreePhone: agentPhone?.benefitPhone1800,
        liveChatURL: process.env.REACT_APP_LIVE_CHAT_URL || '',
      });
    })();
  }, [xauthToken, xsentaraTokenID, loginMemberId, userEncodedData, groupLob, planType, agentPhone]);

  const features: ChatbotFeatureFlags = {
    enableLiveAgent: getFeature?.NewChatBotLiveAgent ?? true,
    enableSatmetrixSurvey: getFeature?.NewChatBotSurvey ?? true,
  };

  return (
    <>
      {getFeature?.NewChatBot &&
        chatBotAiConfig &&
        Object.keys(chatBotAiConfig).length > 0 && (
          <div className={`chatbot_frame`} data-testid={'chatbot-frame'}>
            <App
              onClose={() => {
                setChatBotAiConfig({} as AppAgentConfig);
                setCxoneConfig(null);
                setSessionContext(null);
                setSurveyConfig({} as SatmetrixConfig);
              }}
              onMinimize={onMinimize}
              agentConfig={chatBotAiConfig}
              cxoneConfig={cxoneConfig ?? undefined}
              sessionContext={sessionContext ?? undefined}
              getThreadId={(threadId: string) => threadId}
              surveyConfig={surveyConfig}
              content={t('aiChatbot', { returnObjects: true }) as any}
              isVisible={!minimizeChat}
              serviceNumber={`${agentPhone?.benefitPhone1800}`}
              logger={logger}
              monitoring={monitoring}
              features={features}
              getStoredTranscript={storedTranscripts ?? undefined}
              getStoredSessionId={threadId ?? undefined}
            />
          </div>
        )}
    </>
  );
};

export default AiChatbot;
